<template>
  <iColumn width="hug" gap="none">
    <!-- Single iCL root so data-edit-identifier lands (multi-root drops it).
         Keep this comment INSIDE the root — leading it drops the attr in production only. -->
    <iColumn v-if="!isUserLoggedIn" width="hug" class="nav-action-button">
      <iButton variant="tertiary" @click="redirectToLogin">
        Login
      </iButton>
    </iColumn>
    <!-- In the Studio canvas, render only the trigger: mounting the dropdown's
       iDropdown here leaks its theme/style-overrides into Studio chrome menus. -->
    <div
      v-else-if="renderInEditorCanvas"
      :style="hostWrapperStyle"
    >
      <DynamicComponentProduction
        v-if="embeddedDcId"
        :dynamic-component-id="embeddedDcId"
        :is-production="renderingInProduction"
        :theme-mode="themeMode"
      />
    </div>
    <iDropdownMenu
      v-else
      teleport-to-body
      :items="subMenuItems"
      :show-dropdown="showAvatarDropdown"
      :style-overrides="{
        borderRadius: '12px',
        borderColor: getOptionValue(BorderColor) || 'dark',
        paddingTop: 'none',
        paddingBottom: 'none',
        paddingRight: 'none',
        paddingLeft: 'none',
        dropShadowColor: 'dimmed',
      }"
      :dropdown-style-overrides="{
        borderRadius: '12px',
        borderColor: getOptionValue(BorderColor) || 'dark',
      }"
      :menu-style-overrides="{
        menuItemBorderRadius: '12px',
        menuItemHoverBackgroundColor: getOptionValue(BackgroundColor) || 'subtle',
        menuItemPaddingTop: 'small',
        menuItemPaddingBottom: 'small',
        menuItemPaddingRight: 'standard',
        menuItemPaddingLeft: 'standard',
      }"
      @close="closeDropdown"
      @click="handleAction($event)"
    >
      <template #trigger>
        <div :style="hostWrapperStyle" data-test="user-menu-trigger">
          <DynamicComponentProduction
            v-if="embeddedDcId"
            :dynamic-component-id="embeddedDcId"
            :is-production="renderingInProduction"
            :theme-mode="themeMode"
            @click="toggleDropdown"
          />
        </div>
      </template>
    </iDropdownMenu>
    <Teleport to="body">
      <iModal
        v-if="showUserSettingsModal"
        teleport-to-body
        :show-footer="false"
        :max-width="460"
        :focus-first-element="false"
        :visible="showUserSettingsModal"
        :style-overrides="userSettingsModalStyleOverrides"
        :theme-id="platformThemeId"
        theme-mode="unset"
        title="Account"
        @close="toggleUserSettingsModal"
      >
        <template #header>
          <iRow
            align="between"
            vertical-align="middle"
            width="fill"
            wrap="nowrap"
          >
            <iText font-size="standard" font-weight="dark">
              Account
            </iText>
            <iIcon
              :icon="'close'"
              size="small"
              variant="subtle"
              :style-overrides="modalCloseStyleOverrides"
              @click="toggleUserSettingsModal"
            />
          </iRow>
        </template>
        <UserAccountManagement :app-id="platformAppId" />
        <iColumn v-if="blockedPeopleConfigurationId > 0" width="fill" :style-overrides="{paddingTop: 'large'}">
          <BlockedPeopleSection :configuration-id="blockedPeopleConfigurationId" />
        </iColumn>
      </iModal>
    </Teleport>
  </iColumn>
</template>


<script>
import App from "@bloglovin/inspire-engine/App";
import DynamicComponentProduction
  from "%/dynamic-component-editor/components/DynamicComponentProductionComponent.vue";
import BlockedPeopleSection from "%/dynamic-component-editor/components/story-comments/BlockedPeopleSection.vue";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { USER_BLOCKING_APP } from "%/dynamic-component-editor/constants/story-engagement-constants.mjs";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import { mapActions, mapState } from "pinia";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { MenuConstants, useStyleStore } from "@bloglovin/vue-component-library";
import { useUserStore } from "~/stores/user-store";
import UserAccountManagement from "@/components/UserAccountManagement.vue";
import Margins from "%/dynamic-component-editor/options/margins";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import ShowDropdown from "%/dynamic-component-editor/options/show-dropdown";
import DcHostMixin from "%/dynamic-component-editor/mixins/dc-host-picker-mixin";
import ReferencedComponentId from "%/dynamic-component-editor/options/referenced-component-id";
import ContextBindings from "%/dynamic-component-editor/options/context-bindings";
import {
  DEFAULT_AVATAR_MENU,
  dropdownMenuItems,
  resolveAvatarMenu,
} from "%/dynamic-component-editor/helpers/avatar-menu-rules";

const MSG_AVATAR_MENU_NOT_INJECTED = "UserMenuAndJoin: the page was rendered without the avatarMenu JS var "
  + "(HomeStudioController::injectAvatarMenuJsVar), so the destination's own avatar menu can't be shown; "
  + "showing the built-in Settings, User Profile and Logout items instead";
const MSG_AVATAR_MENU_ITEM_BROKEN = "Avatar menu item left out of the dropdown:";
let reportedAvatarMenuProblems = false;

function resolvedAvatarMenu() {
  const injected = typeof window !== "undefined" ? window.blclient?.data?.avatarMenu : undefined;
  if (injected) {
    return injected;
  }
  // eslint-disable-next-line no-console
  console.error(MSG_AVATAR_MENU_NOT_INJECTED);
  return resolveAvatarMenu(DEFAULT_AVATAR_MENU, [], {}, false);
}

function reportAvatarMenuProblems(problems) {
  if (reportedAvatarMenuProblems) {
    return;
  }
  reportedAvatarMenuProblems = true;
  problems.forEach(problem => {
    // eslint-disable-next-line no-console
    console.error(MSG_AVATAR_MENU_ITEM_BROKEN, problem.message, { itemId: problem.itemId, code: problem.code });
  });
}

export default {
  name: "UserMenuAndJoin",
  components: { UserAccountManagement, DynamicComponentProduction, BlockedPeopleSection },
  mixins: [DynamicElementMixin, DcHostMixin],
  // Provided by DynamicComponentBuilderElement on every canvas element;
  // defaults false off-canvas (staging / published FanWeb) — see computed below.
  inject: {
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
  },
  relatedComponents: ["User Menu And Join"],
  elementPanelSettings: {
    displayName: "User Menu & Join",
    displayIcon: "button",
    elementGroup: "general",
    elementType: "element",
  },
  options: [
    ReferencedComponentId,
    ContextBindings,
    ShowDropdown,
    new BackgroundColor({
      "default": {
        "value": "subtle",
        "source": "theme.background",
      },
    }, false, true, "Submenu Item Hover Background Color"),
    new BorderColor({
      "default": {
        "value": "dark",
        "source": "theme.border",
      },
    }, false, true, "Submenu Border Color"),
  ],
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-height": "hug",
      "default.layout-width": "hug",
    }, {
      "layout-height": {
        disable: true,
        message: "This option is only available in Pro publications.",
      },
      "layout-width": {
        disable: true,
        message: "This option is only available in Pro publications.",
      },
    }),
  ],
  data() {
    return {
      Margins,
      BaseOption,
      BorderColor,
      BackgroundColor,
      defaultComponentName: "User Menu And Join",
      showAvatarDropdown: false,
      showUserSettingsModal: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, [
      "userData",
      "userId",
      "fanUrl",
      "appId",
    ]),
    ...mapState(useConfigurationStore, [
      "isSignupAndLoginEnabled",
    ]),
    isUserLoggedIn() {
      return this.isSignupAndLoginEnabled && this.userId > 0;
    },
    blockedPeopleConfigurationId() {
      const userBlocking = window.blclient?.data?.userBlocking;
      if (!this.isUserLoggedIn || !userBlocking || !App.get(contextualAppName()).isAppNameInScope(USER_BLOCKING_APP)) {
        return 0;
      }
      return userBlocking.configurationId;
    },
    // The Account sheet is inspiration.co chrome, not destination content: it
    // renders on the platform theme so a destination theme cannot break it.
    platformThemeId() {
      return useStyleStore().DEFAULT_THEME_ID;
    },
    platformAppId() {
      return useStyleStore().DEFAULT_APP_ID;
    },
    modalCloseStyleOverrides() {
      return {
        backgroundColor: "secondary.standard",
        borderRadius: "999px",
        paddingTop: "small",
        paddingBottom: "small",
        paddingLeft: "small",
        paddingRight: "small",
      };
    },
    userSettingsModalStyleOverrides() {
      return {
        contentBackgroundColor: "subtle",
        modalBorderRadius: "20px",
        headerPaddingTop: "large",
        headerPaddingBottom: "standard",
        contentPaddingTop: "large",
        contentPaddingBottom: "extraLarge",
      };
    },
    isDropdownEnabled() {
      return this.getOptionValue(ShowDropdown);
    },
    // True when rendered inside the Studio builder canvas (selected or embedded);
    // gates out the dropdown so it can't pollute editor-global dropdown styles.
    renderInEditorCanvas() {
      return this.editMode || this.inBuilderEditScope;
    },
    avatarMenuItems() {
      return resolvedAvatarMenu().items;
    },
    subMenuItems() {
      if (!this.isUserLoggedIn) {
        return [];
      }
      return dropdownMenuItems(this.avatarMenuItems, {
        actionTypes: {
          buttonClick: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          linkInSameWindow: MenuConstants.ACTION_TYPE_LINK_IN_SAME_WINDOW,
          linkInNewWindow: MenuConstants.ACTION_TYPE_LINK_IN_NEW_WINDOW,
        },
        settingsAction: "toggleUserSettingsModal",
        profileHref: this.getRelativeHref("/user-profile"),
        logoutHref: `${this.fanUrl}/logout`,
        relativeHref: href => this.getRelativeHref(href),
      });
    },
  },
  created() {
    if (this.renderInEditorCanvas) {
      return;
    }
    reportAvatarMenuProblems(resolvedAvatarMenu().problems);
  },
  methods: {
    ...mapActions(useGeneralStore, [
      "getRelativeHref",
      "redirectTo",
    ]),
    ...mapActions(useUserStore, [
      "logoutUser",
    ]),
    getSpacingValue(spacingClass, side) {
      return this.getOptionValue(spacingClass, BaseOption.$states.DEFAULT, side) || "none";
    },
    redirectToLogin() {
      if (!this.editMode) {
        this.redirectTo("/login");
      }
    },
    toggleUserSettingsModal() {
      this.showUserSettingsModal = !this.showUserSettingsModal;
    },
    handleAction(event) {
      this[event]();
    },
    toggleDropdown() {
      if (!this.isDropdownEnabled) {
        return;
      }

      if (!this.editMode) {
        this.showAvatarDropdown = !this.showAvatarDropdown;
      }
    },
    closeDropdown() {
      this.showAvatarDropdown = false;
    },
  },
};
</script>
